<template>
  <div class="modeling-drawer-content">
    <!-- 头部 -->
    <div class="drawer-header">
      <div class="drawer-header-left">
        <h2 class="drawer-title">智能本体建模流水线</h2>
        <span class="drawer-step-count">步骤 {{ modelingData.currentStep }}/{{ modelingData.totalSteps }}</span>
      </div>
      <!-- <button class="drawer-close-btn" @click="handleClose">
        <i class="el-icon-close"></i>
      </button> -->
    </div>

    <!-- 进度条 -->
    <div class="drawer-progress-bar">
      <div class="drawer-progress-fill" :style="{ width: stepPercent + '%' }"></div>
    </div>

    <!-- 4个步骤 -->
    <div class="drawer-body">
      <div v-for="s in modelingData.steps" :key="s.id" class="drawer-step" :class="stepBorderClass(s)">
        <!-- 步骤头部 -->
        <div class="step-head" :class="stepHeadBgClass(s)">
          <div class="step-head-left">
            <span class="step-num" :class="stepNumClass(s)">{{ s.id }}</span>
            <span class="step-title">{{ s.title }}</span>
          </div>
          <span class="step-status" :class="stepStatusClass(s)">{{ stepStatusLabel(s) }}</span>
        </div>

        <!-- 步骤主体 -->
        <div class="step-body">
          <!-- Step 1 未选：行业选择网格 -->
          <div v-if="s.id === 1 && s.status === 'in_progress'" class="industry-grid">
            <div v-for="ind in industryLibraries" :key="ind.id" class="industry-card" @click="selectIndustry(ind.id)">
              <div class="industry-card-icon">
                <i :class="ind.icon"></i>
              </div>
              <div class="industry-card-name">{{ ind.label }}</div>
              <div class="industry-card-desc">{{ ind.value }}</div>
              <span class="industry-card-action">选择此模板</span>
            </div>
          </div>

          <!-- 其他情况：详情网格 -->
          <div v-else-if="s.detail && s.detail.length" class="detail-grid">
            <div v-for="d in s.detail" :key="d.label" class="detail-item" :class="detailItemBgClass(d)">
              <div class="detail-label">{{ d.label }}</div>
              <div class="detail-value" :class="detailValueClass(d)">
                <i v-if="d.status === 'loading'" class="el-icon-loading"></i>
                {{ d.value }}
              </div>
            </div>
          </div>

          <!-- Step 4 预览结果审核 -->
          <div v-if="s.id === 4 && s.status === 'completed'" class="review-section">
            <div class="review-graph-tabs">
              <button v-for="sc in sceneList" :key="sc.id" class="review-tab-btn" :class="{ active: reviewScene === sc.id }" @click="switchReviewScene(sc.id)">
                {{ sc.name }}
              </button>
            </div>
            <div class="review-graph-wrap">
              <iframe :src="ontologyMapUrl" frameborder="0" class="review-graph-iframe" />
            </div>
            <div class="review-legend">
              <span class="legend-dot" style="background: #4a5ff3"></span> 业务对象 <span class="legend-line"></span> 业务关系
              <span class="legend-divider">|</span>
              <span class="legend-tip">切换上方标签查看各场景图谱</span>
            </div>
            <div class="review-actions">
              <button class="review-btn reject" @click="rejectReview">驳回</button>
              <button class="review-btn approve" @click="approveReview">全部通过</button>
            </div>
          </div>

          <!-- 摘要 -->
          <div v-if="s.summary" class="step-summary">{{ s.summary }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const SCENARIO_LIST = [
  { id: 'production', name: '生产场景', icon: 'el-icon-s-operation', color: '#4a5ff3' },
  { id: 'finance', name: '财务场景', icon: 'el-icon-s-finance', color: '#52c41a' },
  { id: 'operations', name: '运营场景', icon: 'el-icon-data-analysis', color: '#ff7d00' },
  { id: 'rd', name: '研发场景', icon: 'el-icon-s-platform', color: '#a855f7' },
  { id: 'admin', name: '行政场景', icon: 'el-icon-s-custom', color: '#06b6d4' },
  { id: 'safety', name: '安全场景', icon: 'el-icon-warning', color: '#ef4444' }
]

const SCENARIO_GRAPHS = {
  production: {
    name: '生产场景',
    objects: ['生产工单', '产品', '物料', '产线', '车间', '设备', '供应商'],
    relations: [
      { source: '生产工单', target: '产品', type: '生产' },
      { source: '生产工单', target: '产线', type: '执行于' },
      { source: '产线', target: '车间', type: '属于' },
      { source: '产品', target: '物料', type: 'BOM构成' },
      { source: '物料', target: '生产工单', type: '用于' },
      { source: '设备', target: '产线', type: '部署于' }
    ]
  },
  finance: {
    name: '财务场景',
    objects: ['财务报表', '预算', '成本中心', '收入'],
    relations: [
      { source: '收入', target: '财务报表', type: '构成' },
      { source: '预算', target: '财务报表', type: '汇总至' },
      { source: '成本中心', target: '预算', type: '关联' }
    ]
  },
  operations: {
    name: '运营场景',
    objects: ['客户', '销售订单', '生产工单', '产品', '物料', '发货单', '财务报表', '预算'],
    relations: [
      { source: '客户', target: '销售订单', type: '下达' },
      { source: '销售订单', target: '产品', type: '订购' },
      { source: '销售订单', target: '生产工单', type: '触发' },
      { source: '生产工单', target: '产品', type: '生产' },
      { source: '产品', target: '物料', type: 'BOM构成' },
      { source: '发货单', target: '销售订单', type: '关联' },
      { source: '预算', target: '财务报表', type: '汇总至' }
    ]
  },
  rd: {
    name: '研发场景',
    objects: ['研发项目', '实验数据', '产品', '物料'],
    relations: [
      { source: '研发项目', target: '实验数据', type: '产出' },
      { source: '产品', target: '物料', type: 'BOM构成' }
    ]
  },
  admin: {
    name: '行政场景',
    objects: ['员工', '部门', '采购订单'],
    relations: [{ source: '员工', target: '部门', type: '属于' }]
  },
  safety: {
    name: '安全场景',
    objects: ['安全事件', '风险点', '设备', '产线'],
    relations: [
      { source: '安全事件', target: '风险点', type: '关联' },
      { source: '设备', target: '产线', type: '部署于' }
    ]
  }
}

import { BIGDATA_PATHS } from './bigdataPath'

export default {
  name: 'ModelingDrawer',
  props: {
    value: { type: Boolean, default: false }
  },
  data() {
    return {
      selectedIndustry: null,
      reviewScene: 'production',
      modelingTimers: [],
      modelingData: this.createModelingData()
    }
  },
  computed: {
    sceneList() {
      return SCENARIO_LIST
    },
    currentSceneGraph() {
      return SCENARIO_GRAPHS[this.reviewScene] || SCENARIO_GRAPHS.production
    },
    stepPercent() {
      return Math.round((this.modelingData.currentStep / this.modelingData.totalSteps) * 100)
    },
    industryLibraries() {
      return [
        { id: 'manufacturing', label: '制造业', value: '13 个对象 · 16 条关系', icon: 'el-icon-setting' },
        { id: 'chemical', label: '化工行业', value: '10 个对象 · 12 条关系', icon: 'el-icon-s-platform' },
        { id: 'pcba', label: 'PCBA行业', value: '8 个对象 · 10 条关系', icon: 'el-icon-s-grid' }
      ]
    },
    ontologyMapUrl() {
      return BIGDATA_PATHS.ontologyMap
    },
    svgNodes() {
      const graph = this.currentSceneGraph
      if (!graph) return []
      const layers = this.computeLayout(graph.objects, graph.relations)
      const nodeW = 90
      const nodeH = 28
      const gapX = 40
      const gapY = 10
      const nodes = []
      const svgH = this.svgSize.h
      Object.keys(layers)
        .sort((a, b) => Number(a) - Number(b))
        .forEach((l) => {
          const list = layers[l]
          const totalH = list.length * (nodeH + gapY) - gapY
          const startY = (svgH - totalH) / 2
          list.forEach((name, i) => {
            nodes.push({
              label: name,
              x: Number(l) * (nodeW + gapX) + 20,
              y: startY + i * (nodeH + gapY)
            })
          })
        })
      return nodes
    },
    svgEdges() {
      const graph = this.currentSceneGraph
      if (!graph) return []
      const nodes = this.svgNodes
      const posMap = {}
      nodes.forEach((n) => {
        posMap[n.label] = { x: n.x, y: n.y }
      })
      const nodeW = 90
      const nodeH = 28
      return graph.relations.map((r) => {
        const src = posMap[r.source]
        const tgt = posMap[r.target]
        if (!src || !tgt) return { path: '', labelX: 0, labelY: 0, label: r.type }
        const sx = src.x + nodeW
        const sy = src.y + nodeH / 2
        const tx = tgt.x
        const ty = tgt.y + nodeH / 2
        const mx = (sx + tx) / 2
        return {
          path: `M${sx},${sy} C${mx},${sy} ${mx},${ty} ${tx},${ty}`,
          labelX: mx,
          labelY: (sy + ty) / 2 - 4,
          label: r.type
        }
      })
    },
    svgSize() {
      const graph = this.currentSceneGraph
      if (!graph || !graph.objects.length) return { w: 0, h: 0 }
      const layers = this.computeLayout(graph.objects, graph.relations)
      const nodeW = 90
      const gapX = 40
      const gapY = 10
      const nodeH = 28
      const maxInLayer = Math.max(...Object.keys(layers).map((l) => layers[l].length))
      const layerCount = Object.keys(layers).length
      return {
        w: layerCount * (nodeW + gapX) + 40,
        h: Math.max(220, maxInLayer * (nodeH + gapY) + 20)
      }
    }
  },
  methods: {
    handleClose() {
      this.clearTimers()
      this.modelingData = this.createModelingData()
      this.selectedIndustry = null
      this.reviewScene = 'production'
      this.$emit('input', false)
    },

    createModelingData() {
      return {
        currentStep: 1,
        totalSteps: 4,
        steps: [
          {
            id: 1,
            title: '选择行业本体模型库',
            status: 'in_progress',
            detail: [],
            summary: '选择一个行业本体模型库作为模板'
          },
          {
            id: 2,
            title: '自动匹配业务对象和指标',
            status: 'pending',
            detail: [
              { label: '业务对象', value: '待匹配', status: 'inactive' },
              { label: '指标定义', value: '待匹配', status: 'inactive' },
              { label: '属性映射', value: '待映射', status: 'inactive' }
            ],
            summary: '智能匹配业务对象与关联指标'
          },
          {
            id: 3,
            title: '生成本体模型',
            status: 'pending',
            detail: [
              { label: '业务对象', value: '待生成', status: 'inactive' },
              { label: '业务关系', value: '待生成', status: 'inactive' },
              { label: '属性映射', value: '待生成', status: 'inactive' }
            ],
            summary: '基于匹配结果构建企业级本体模型'
          },
          {
            id: 4,
            title: '预览结果审核',
            status: 'pending',
            detail: [],
            summary: '审核本体模型'
          }
        ]
      }
    },

    stepBorderClass(s) {
      if (s.id === 1 && s.status === 'in_progress') return 'step-border-primary'
      if (s.status === 'completed') return 'step-border-success'
      if (s.status === 'in_progress') return 'step-border-primary'
      return 'step-border-pending'
    },

    stepHeadBgClass(s) {
      if (s.id === 1 && s.status === 'in_progress') return 'step-head-primary'
      if (s.status === 'completed') return 'step-head-success'
      if (s.status === 'in_progress') return 'step-head-primary'
      return 'step-head-pending'
    },

    stepNumClass(s) {
      if (s.id === 1 && s.status === 'in_progress') return 'step-num-primary'
      if (s.status === 'completed') return 'step-num-success'
      if (s.status === 'in_progress') return 'step-num-primary'
      return 'step-num-pending'
    },

    stepStatusClass(s) {
      if (s.status === 'completed') return 'step-status-completed'
      if (s.id === 1 && s.status === 'in_progress') return 'step-status-selecting'
      if (s.status === 'in_progress') return 'step-status-in-progress'
      return 'step-status-pending'
    },

    stepStatusLabel(s) {
      if (s.status === 'completed') return '已完成'
      if (s.id === 1 && s.status === 'in_progress') return '请选择'
      if (s.status === 'in_progress') return '进行中'
      return '待处理'
    },

    detailItemBgClass(d) {
      if (d.status === 'success') return 'detail-bg-success'
      if (d.status === 'loading') return 'detail-bg-loading'
      return 'detail-bg-inactive'
    },

    detailValueClass(d) {
      if (d.status === 'success') return 'detail-value-success'
      if (d.status === 'loading') return 'detail-value-loading'
      return 'detail-value-inactive'
    },

    selectIndustry(industry) {
      if (this.selectedIndustry) return
      this.selectedIndustry = industry
      const names = { manufacturing: '制造业', chemical: '化工行业', pcba: 'PCBA行业' }
      const counts = { manufacturing: '13个对象·16条关系', chemical: '10个对象·12条关系', pcba: '8个对象·10条关系' }
      const name = names[industry] || industry

      const s1 = this.modelingData.steps[0]
      s1.status = 'completed'
      s1.detail = [
        { label: '选定行业', value: name, status: 'success' },
        { label: '模板版本', value: 'v2.1', status: 'success' },
        { label: '覆盖', value: counts[industry], status: 'success' }
      ]
      s1.summary = '已选定「' + name + '」本体模板'

      this.modelingData.currentStep = 2
      const s2 = this.modelingData.steps[1]
      s2.status = 'in_progress'
      s2.detail = [
        { label: '业务对象', value: '匹配中', status: 'loading' },
        { label: '指标定义', value: '待匹配', status: 'inactive' },
        { label: '属性映射', value: '待映射', status: 'inactive' }
      ]
      s2.summary = '正在自动匹配...'

      this.$forceUpdate()
      this.runStep2()
    },

    runStep2() {
      const d = 700
      const res2 = [
        { label: '业务对象', value: '13/13', status: 'success' },
        { label: '指标定义', value: '26 项', status: 'success' },
        { label: '属性映射', value: '46 个', status: 'success' }
      ]
      const s2 = this.modelingData.steps[1]

      res2.forEach((r, i) => {
        const t = setTimeout(() => {
          s2.detail[i] = { label: r.label, value: r.value, status: 'success' }
          if (i + 1 < res2.length) {
            s2.detail[i + 1] = { label: res2[i + 1].label, value: '匹配中', status: 'loading' }
          }
          s2.summary = '正在匹配 (' + (i + 1) + '/' + res2.length + ')'
          if (i === res2.length - 1) {
            s2.status = 'completed'
            s2.summary = '匹配完成：13对象、26指标、46属性映射'
            this.$forceUpdate()
            const t2 = setTimeout(() => this.runStep3(), 500)
            this.modelingTimers.push(t2)
          }
          this.$forceUpdate()
        }, d * (i + 1))
        this.modelingTimers.push(t)
      })
    },

    runStep3() {
      this.modelingData.currentStep = 3
      const s3 = this.modelingData.steps[2]
      s3.status = 'in_progress'
      s3.detail = [
        { label: '业务对象', value: '生成中', status: 'loading' },
        { label: '业务关系', value: '待生成', status: 'inactive' },
        { label: '属性映射', value: '待生成', status: 'inactive' }
      ]
      s3.summary = '正在构建本体模型...'
      this.$forceUpdate()

      const d = 700
      const res3 = [
        { label: '业务对象', value: '13', status: 'success' },
        { label: '业务关系', value: '16 条', status: 'success' },
        { label: '属性映射', value: '46 个', status: 'success' }
      ]
      const s3ref = this.modelingData.steps[2]

      res3.forEach((r, i) => {
        const t = setTimeout(() => {
          s3ref.detail[i] = { label: r.label, value: r.value, status: 'success' }
          if (i + 1 < res3.length) {
            s3ref.detail[i + 1] = { label: res3[i + 1].label, value: '生成中', status: 'loading' }
          }
          s3ref.summary = '正在构建 (' + (i + 1) + '/' + res3.length + ')'
          if (i === res3.length - 1) {
            s3ref.status = 'completed'
            s3ref.summary = '构建完成：13对象、16关系、46属性映射'
            this.$forceUpdate()
            const t2 = setTimeout(() => this.runStep4(), 500)
            this.modelingTimers.push(t2)
          }
          this.$forceUpdate()
        }, d * (i + 1))
        this.modelingTimers.push(t)
      })
    },

    runStep4() {
      this.modelingData.currentStep = 4
      const s4 = this.modelingData.steps[3]
      s4.status = 'completed'
      s4.summary = '本体模型已生成，共6个场景图谱待审核'
      this.$forceUpdate()
    },

    switchReviewScene(sceneId) {
      this.reviewScene = sceneId
    },

    rejectReview() {
      this.$message({ message: '已驳回，请重新调整模型配置', type: 'warning' })
    },

    approveReview() {
      this.$message({ message: '本体模型已全部审核通过', type: 'success' })
    },

    computeLayout(objects, relations) {
      const children = {}
      const inDegree = {}
      objects.forEach((o) => {
        children[o] = []
        inDegree[o] = 0
      })
      relations.forEach((r) => {
        if (children[r.source]) children[r.source].push(r.target)
        if (inDegree[r.target] !== undefined) inDegree[r.target]++
      })
      const roots = objects.filter((o) => inDegree[o] === 0)
      const layer = {}
      const queue = [...roots]
      roots.forEach((r) => {
        layer[r] = 0
      })
      let front = 0
      while (front < queue.length) {
        const node = queue[front++]
        const ll = (layer[node] || 0) + 1
        ;(children[node] || []).forEach((child) => {
          if (!(child in layer) || layer[child] < ll) {
            layer[child] = ll
            queue.push(child)
          }
        })
      }
      const layers = {}
      objects.forEach((o) => {
        const l = layer[o] !== undefined ? layer[o] : 999
        if (!layers[l]) layers[l] = []
        layers[l].push(o)
      })
      return layers
    },

    clearTimers() {
      this.modelingTimers.forEach((t) => clearTimeout(t))
      this.modelingTimers = []
    }
  },
  beforeDestroy() {
    this.clearTimers()
  }
}
</script>

<style lang="scss" scoped>
.modeling-drawer-content {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #fff;
}

/* ── 头部 ── */
.drawer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px 0;
  flex-shrink: 0;

  .drawer-header-left {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .drawer-title {
    font-size: 16px;
    font-weight: 600;
    color: #1a1a2e;
    margin: 0;
  }

  .drawer-step-count {
    font-size: 12px;
    color: #94a3b8;
  }

  .drawer-close-btn {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    border: none;
    background: transparent;
    color: #999;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;

    &:hover {
      background: #f5f5f5;
      color: #333;
    }
    i {
      font-size: 16px;
    }
  }
}

/* ── 进度条 ── */
.drawer-progress-bar {
  margin: 16px 24px 0;
  height: 6px;
  background: #f1f5f9;
  border-radius: 999px;
  overflow: hidden;
  flex-shrink: 0;

  .drawer-progress-fill {
    height: 100%;
    background: linear-gradient(90deg, #4a5ff3, #52c41a);
    border-radius: 999px;
    transition: width 0.5s ease;
  }
}

/* ── 主体滚动 ── */
.drawer-body {
  flex: 1;
  overflow-y: auto;
  padding: 16px 24px 24px;
  scrollbar-width: thin;
  scrollbar-color: transparent transparent;

  &::-webkit-scrollbar {
    width: 5px;
    background: transparent;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: transparent;
    border-radius: 3px;
  }

  &:hover {
    scrollbar-color: rgba(148, 163, 184, 0.5) transparent;
    &::-webkit-scrollbar-thumb {
      background: rgba(148, 163, 184, 0.5);
    }
  }
}

/* ── 步骤卡片 ── */
.drawer-step {
  border: 2px solid;
  border-radius: 12px;
  margin-bottom: 12px;
  overflow: hidden;

  &.step-border-primary {
    border-color: #3370ff;
  }
  &.step-border-success {
    border-color: #52c41a;
  }
  &.step-border-pending {
    border-color: #e2e8f0;
  }

  .step-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;

    &.step-head-primary {
      background: #eef2ff;
    }
    &.step-head-success {
      background: #f0fdf4;
    }
    &.step-head-pending {
      background: #f8fafc;
    }

    .step-head-left {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .step-num {
      width: 28px;
      height: 28px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 12px;
      font-weight: 700;
      color: #fff;
      flex-shrink: 0;

      &.step-num-primary {
        background: #3370ff;
      }
      &.step-num-success {
        background: #52c41a;
      }
      &.step-num-pending {
        background: #cbd5e1;
      }
    }

    .step-title {
      font-size: 14px;
      font-weight: 600;
      color: #334155;
    }

    .step-status {
      font-size: 12px;
      font-weight: 500;
      flex-shrink: 0;

      &.step-status-completed {
        color: #52c41a;
      }
      &.step-status-selecting {
        color: #3370ff;
      }
      &.step-status-in-progress {
        color: #3b82f6;
      }
      &.step-status-pending {
        color: #94a3b8;
      }
    }
  }

  .step-body {
    padding: 12px 16px;
    background: #fff;
  }

  .step-summary {
    font-size: 12px;
    color: #64748b;
    line-height: 1.5;
    margin-top: 8px;
  }
}

/* ── 行业选择网格 ── */
.industry-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;

  .industry-card {
    border: 2px solid #e2e8f0;
    border-radius: 12px;
    padding: 16px;
    text-align: center;
    cursor: pointer;
    background: #fff;
    transition: all 0.15s;

    &:hover {
      border-color: #3370ff;
      box-shadow: 0 4px 6px -1px rgba(74, 95, 243, 0.1);
      transform: translateY(-2px);
    }

    .industry-card-icon {
      width: 40px;
      height: 40px;
      border-radius: 12px;
      background: linear-gradient(135deg, #4e86ff, #4a5ff3);
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto 10px;

      i {
        color: #fff;
        font-size: 20px;
      }
    }

    .industry-card-name {
      font-size: 14px;
      font-weight: 600;
      color: #334155;
      margin-bottom: 4px;
    }

    .industry-card-desc {
      font-size: 12px;
      color: #94a3b8;
      margin-bottom: 10px;
    }

    .industry-card-action {
      display: inline-block;
      font-size: 11px;
      font-weight: 500;
      color: #3370ff;
      background: #eef2ff;
      border-radius: 9999px;
      padding: 4px 12px;
    }
  }
}

/* ── 详情网格 ── */
.detail-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;

  .detail-item {
    padding: 10px 12px;
    border-radius: 8px;

    &.detail-bg-success {
      background: #f0fdf4;
    }
    &.detail-bg-loading {
      background: #eef2ff;
    }
    &.detail-bg-inactive {
      background: #f8fafc;
    }

    .detail-label {
      font-size: 11px;
      color: #94a3b8;
      margin-bottom: 4px;
    }

    .detail-value {
      font-size: 13px;
      font-weight: 600;

      &.detail-value-success {
        color: #16a34a;
      }
      &.detail-value-loading {
        color: #3370ff;
      }
      &.detail-value-inactive {
        color: #94a3b8;
      }

      i {
        margin-right: 4px;
      }
    }
  }
}

/* ── 预览审核 ── */
.review-section {
  margin-top: 0;

  .review-graph-tabs {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 12px;

    .review-tab-btn {
      padding: 5px 14px;
      border-radius: 8px;
      font-size: 12px;
      font-weight: 500;
      border: 1.5px solid #e2e8f0;
      background: #fff;
      color: #64748b;
      cursor: pointer;
      transition: all 0.15s;

      &:hover {
        background: #eef2ff;
        color: #3370ff;
        border-color: #3370ff;
      }

      &.active {
        background: #eef2ff;
        color: #3370ff;
        border-color: #3370ff;
      }
    }
  }

  .review-graph-wrap {
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    overflow: hidden;
    height: 420px;

    .review-graph-iframe {
      width: 100%;
      height: 100%;
      display: block;
      border: none;
    }
  }

  .review-legend {
    font-size: 11px;
    color: #94a3b8;
    margin-top: 8px;
    display: flex;
    align-items: center;
    gap: 6px;

    .legend-dot {
      display: inline-block;
      width: 8px;
      height: 8px;
      border-radius: 50%;
    }
    .legend-line {
      display: inline-block;
      width: 16px;
      height: 2px;
      background: #94a3b8;
    }
    .legend-divider {
      color: #cbd5e1;
    }
    .legend-tip {
      color: #cbd5e1;
    }
  }

  .review-actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid #f1f5f9;

    .review-btn {
      padding: 8px 18px;
      border-radius: 8px;
      font-size: 12px;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.2s;
      border: none;

      &.reject {
        border: 1.5px solid #e2e8f0;
        background: #fff;
        color: #64748b;
        &:hover {
          background: #f8fafc;
        }
      }

      &.approve {
        background: #3370ff;
        color: #fff;
        &:hover {
          background: #3b4fd9;
        }
      }
    }
  }
}
</style>
